import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  UncontrolledAlert,
} from "reactstrap";

// --- DOCX & FILE SAVER IMPORTS ---
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table as DocxTable,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";

// --- IMPORT YOUR ASSETS HERE ---
import logoImg from "../../assets/images/invoice/limo_genie_logo.jpg";
import stampImg from "../../assets/images/invoice/limo_stamp.jpg";
import signatureImg from "../../assets/images/invoice/signature.png";

// Helper function to format today's date (e.g., "24 Sept 2026")
const getFormattedTodayDate = () => {
  const today = new Date();
  const day = today.getDate();
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"
  ];
  const month = monthNames[today.getMonth()];
  const year = today.getFullYear();
  return `${day} ${month} ${year}`;
};

// --- AUTOMATIC NUMBER TO WORDS CONVERTER ---
const numberToWords = (num) => {
  if (num === 0 || !num || isNaN(num)) return "Zero";

  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + a[n % 10] : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 !== 0 ? " " + inWords(n % 100) : "")
      );
    if (n < 1000000)
      return (
        inWords(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 !== 0 ? " " + inWords(n % 1000) : "")
      );
    if (n < 1000000000)
      return (
        inWords(Math.floor(n / 1000000)) +
        " Million" +
        (n % 1000000 !== 0 ? " " + inWords(n % 1000000) : "")
      );
    return "";
  };

  const amount = parseFloat(num);
  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);

  let dollarsInWords = dollars > 0 ? inWords(dollars) : "Zero";
  let centsInWords = cents > 0 ? " and Cents " + inWords(cents) : "";

  return `Singapore Dollars ${dollarsInWords}${centsInWords} Only`;
};

const ManualInvoice = () => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form State (Dynamic Data) with today's date auto-populated
  const [invoiceData, setInvoiceData] = useState({
    requestorName: "RB Corp Pte Ltd",
    requestorAddress:
      "T/A Intercontinental Singapore Robertson Quay\n1 Nanson Road\nSingapore 238909",
    attention: "Accounts Payable",
    invoiceDate: getFormattedTodayDate(),
    invoiceNo: "LG/085-26/ICRQ-AK",
    customerRef: "ICRQ",
    paymentTerms: "30 days from invoice date",
    items: [
      {
        id: 1,
        description:
          "Being limousine service rendered from 1 August 2026 to 31 August 2026",
        quantity: 24,
        unit: "rides",
        unitPrice: "",
        totalAmount: 2330.0,
      },
    ],
  });

  // Auto-populated Static Data
  const staticData = {
    companyName: "LIMOGENIE PTE LTD",
    companyAddress:
      "80 PAYA LEBAR ROAD, #07-54 PAYA LEBAR SQUARE, SINGAPORE 409051",
    accountNumber: "0721373319 (SGD)",
    beneficiaryBank: "LIMOGENIE PTE LTD",
    swiftCode: "DBSSSGSG",
    bankAddress: "128 BEACH ROAD, GUOCO MIDTOWN #21-01 SINGAPORE 189773",
    intermediaryBank: "DBS BANK",
    intermediarySwift: "7171",
    intermediaryAddress:
      "88 MARKET STREET, LEVEL 28, CAPITASPRING SINGAPORE 048948",
    bankCode: "7171",
    footerInfo: {
      address: "60 Paya Lebar Road, #06-28 Paya Lebar Square, Singapore 409051",
      tel: "+65 85695015",
      regNo: "202318171D",
    },
    signatureTitle: "Authorised Signature",
  };

  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  useEffect(() => {
    const userRole = localStorage.getItem("userType") || localStorage.getItem("user_type");
    const userId = localStorage.getItem("userid");

    // Strict access restriction
    if (userRole === "1" && userId === "655b857a643bcc0b1027bd79") {
      setIsSuperAdmin(true);
    } else {
      setIsSuperAdmin(false);
    }
    setLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index][field] = value;

    const qty = parseFloat(updatedItems[index].quantity) || 0;
    const price = parseFloat(updatedItems[index].unitPrice);

    // If quantity or unit price changes, compute total amount if unit price is set
    if (field === "quantity" || field === "unitPrice") {
      if (updatedItems[index].unitPrice !== "" && !isNaN(price)) {
        updatedItems[index].totalAmount = parseFloat((qty * price).toFixed(2));
      }
    }

    // If total amount is entered directly, reverse-calculate unit price if qty > 0
    if (field === "totalAmount") {
      const total = parseFloat(value);
      if (!isNaN(total) && qty > 0) {
        updatedItems[index].unitPrice = (total / qty).toFixed(2);
      }
    }

    setInvoiceData((prev) => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    setInvoiceData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now(),
          description: "",
          quantity: 1,
          unit: "rides",
          unitPrice: "",
          totalAmount: 0,
        },
      ],
    }));
  };

  const removeItem = (index) => {
    const updatedItems = invoiceData.items.filter((_, i) => i !== index);
    setInvoiceData((prev) => ({ ...prev, items: updatedItems }));
  };

  const calculateTotal = () => {
    return invoiceData.items.reduce(
      (sum, item) => sum + (parseFloat(item.totalAmount) || 0),
      0
    );
  };

  // Dynamically compute amount in words based on calculated total
  const amountInWordsText = numberToWords(calculateTotal());

  const handleSubmit = (e) => {
    e.preventDefault();
    setAlert({ show: true, message: "Manual Invoice generated successfully!", type: "success" });
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    const pdfFileName = invoiceData.customerRef || invoiceData.invoiceNo || "Invoice";
    document.title = pdfFileName.replace(/[/\\?%*:|"<>]/g, "-");

    window.print();

    document.title = originalTitle;
  };

  // --- GENERATE & DOWNLOAD WORD DOCUMENT (.DOCX) ---
  const handleDownloadDocx = async () => {
    try {
      // Helper function to fetch image as ArrayBuffer for docx ImageRun
      const fetchImageAsBuffer = async (url) => {
        try {
          const resp = await fetch(url);
          if (!resp.ok) return null;
          return await resp.arrayBuffer();
        } catch {
          return null;
        }
      };

      const logoBuffer = await fetchImageAsBuffer(logoImg);
      const stampBuffer = await fetchImageAsBuffer(stampImg);
      const signatureBuffer = await fetchImageAsBuffer(signatureImg);

      const tableBorders = {
        top: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
        left: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
        right: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
        insideVertical: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
      };

      const doc = new Document({
        sections: [
          {
            properties: {
              page: {
                margin: { top: 720, bottom: 720, left: 720, right: 720 }, // ~0.5 inch margins
              },
            },
            children: [
              // 1. Header (Logo + Title)
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NONE },
                  bottom: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE },
                },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        children: [
                          logoBuffer
                            ? new Paragraph({
                                children: [
                                  new ImageRun({
                                    data: logoBuffer,
                                    transformation: { width: 140, height: 45 },
                                  }),
                                ],
                              })
                            : new Paragraph({
                                children: [new TextRun({ text: "LimoGenie", bold: true, size: 28 })],
                              }),
                        ],
                      }),
                      new TableCell({
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        children: [
                          new Paragraph({
                            alignment: AlignmentType.RIGHT,
                            children: [
                              new TextRun({
                                text: "INVOICE",
                                bold: true,
                                size: 36,
                                font: "Arial",
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),

              new Paragraph({ text: "", spacing: { after: 120 } }),

              // 2. Customer Info & Meta Info Table
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NONE },
                  bottom: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE },
                },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        width: { size: 55, type: WidthType.PERCENTAGE },
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({ text: `To: ${invoiceData.requestorName}`, bold: true, size: 18 }),
                            ],
                          }),
                          ...invoiceData.requestorAddress.split("\n").map(
                            (line) =>
                              new Paragraph({
                                children: [new TextRun({ text: line, size: 18 })],
                              })
                          ),
                          ...(invoiceData.attention
                            ? [
                                new Paragraph({
                                  children: [
                                    new TextRun({ text: "Attention: ", bold: true, size: 18 }),
                                    new TextRun({ text: invoiceData.attention, size: 18 }),
                                  ],
                                }),
                              ]
                            : []),
                        ],
                      }),
                      new TableCell({
                        width: { size: 45, type: WidthType.PERCENTAGE },
                        children: [
                          new DocxTable({
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            borders: tableBorders,
                            rows: [
                              new TableRow({
                                children: [
                                  new TableCell({
                                    width: { size: 45, type: WidthType.PERCENTAGE },
                                    shading: { fill: "F0F0F0" },
                                    children: [new Paragraph({ children: [new TextRun({ text: "Date:", bold: true, size: 16 })] })],
                                  }),
                                  new TableCell({
                                    width: { size: 55, type: WidthType.PERCENTAGE },
                                    children: [new Paragraph({ children: [new TextRun({ text: invoiceData.invoiceDate, size: 16 })] })],
                                  }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({
                                    shading: { fill: "F0F0F0" },
                                    children: [new Paragraph({ children: [new TextRun({ text: "Inv No.:", bold: true, size: 16 })] })],
                                  }),
                                  new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: invoiceData.invoiceNo, size: 16 })] })],
                                  }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({
                                    shading: { fill: "F0F0F0" },
                                    children: [new Paragraph({ children: [new TextRun({ text: "Customer ref:", bold: true, size: 16 })] })],
                                  }),
                                  new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: invoiceData.customerRef, size: 16 })] })],
                                  }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),

              new Paragraph({ text: "", spacing: { after: 120 } }),

              // 3. Line Items Table
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: tableBorders,
                rows: [
                  // Table Header
                  new TableRow({
                    children: [
                      new TableCell({
                        width: { size: 8, type: WidthType.PERCENTAGE },
                        shading: { fill: "F0F0F0" },
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "S/no", bold: true, size: 16 })] })],
                      }),
                      new TableCell({
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        shading: { fill: "F0F0F0" },
                        children: [new Paragraph({ children: [new TextRun({ text: "Description", bold: true, size: 16 })] })],
                      }),
                      new TableCell({
                        width: { size: 15, type: WidthType.PERCENTAGE },
                        shading: { fill: "F0F0F0" },
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Quantity", bold: true, size: 16 })] })],
                      }),
                      new TableCell({
                        width: { size: 17, type: WidthType.PERCENTAGE },
                        shading: { fill: "F0F0F0" },
                        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: "Unit Price SGD", bold: true, size: 16 })] })],
                      }),
                      new TableCell({
                        width: { size: 20, type: WidthType.PERCENTAGE },
                        shading: { fill: "F0F0F0" },
                        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: "Total Amount SGD", bold: true, size: 16 })] })],
                      }),
                    ],
                  }),
                  // Dynamic Items
                  ...invoiceData.items.map(
                    (item, index) =>
                      new TableRow({
                        children: [
                          new TableCell({
                            children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${index + 1}`, size: 16 })] })],
                          }),
                          new TableCell({
                            children: item.description.split("\n").map(
                              (descLine) => new Paragraph({ children: [new TextRun({ text: descLine, size: 16 })] })
                            ),
                          }),
                          new TableCell({
                            children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${item.quantity} ${item.unit}`, size: 16 })] })],
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                  new TextRun({
                                    text: item.unitPrice !== "" && !isNaN(Number(item.unitPrice))
                                      ? Number(item.unitPrice).toFixed(2)
                                      : "",
                                    size: 16,
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                  new TextRun({
                                    text: Number(item.totalAmount || 0).toLocaleString("en-SG", { minimumFractionDigits: 2 }),
                                    size: 16,
                                  }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      })
                  ),
                  // Total Row
                  new TableRow({
                    children: [
                      new TableCell({
                        columnSpan: 4,
                        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: "Total Amount", bold: true, size: 16 })] })],
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            alignment: AlignmentType.RIGHT,
                            children: [
                              new TextRun({
                                text: calculateTotal().toLocaleString("en-SG", { minimumFractionDigits: 2 }),
                                bold: true,
                                size: 16,
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),

              new Paragraph({ text: "", spacing: { after: 80 } }),

              // 4. Amount in Words
              new Paragraph({
                children: [
                  new TextRun({ text: `(In words: ${amountInWordsText})`, bold: true, size: 16 }),
                ],
                spacing: { after: 120 },
              }),

              // 5. Payment Terms & Stamp / Signature Block
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NONE },
                  bottom: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE },
                },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        children: [
                          new Paragraph({ children: [new TextRun({ text: "Payment Term:", bold: true, underline: {}, size: 16 })] }),
                          new Paragraph({ children: [new TextRun({ text: invoiceData.paymentTerms, size: 16 })] }),
                        ],
                      }),
                      new TableCell({
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        children: [
                          new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                              ...(stampBuffer ? [new ImageRun({ data: stampBuffer, transformation: { width: 55, height: 55 } })] : []),
                              ...(signatureBuffer ? [new ImageRun({ data: signatureBuffer, transformation: { width: 90, height: 35 } })] : []),
                            ],
                          }),
                          new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [new TextRun({ text: staticData.signatureTitle, bold: true, size: 16 })],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),

              new Paragraph({ text: "", spacing: { after: 120 } }),

              // 6. Beneficiary & Bank Details
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: tableBorders,
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        shading: { fill: "F9F9F9" },
                        children: [
                          new Paragraph({
                            children: [new TextRun({ text: "Please make payment to:", bold: true, underline: {}, size: 16 })],
                            spacing: { after: 60 },
                          }),
                          new DocxTable({
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                            rows: [
                              new TableRow({
                                children: [
                                  new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: "Beneficiary Details", bold: true, size: 15 })] })] }),
                                  new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: "Company Name", size: 15 })] })] }),
                                  new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.companyName}`, size: 15 })] })] }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({ children: [new Paragraph({ text: "" })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Company Address", size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.companyAddress}`, size: 15 })] })] }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Bank Details", bold: true, size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Account Name", size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.beneficiaryBank}`, size: 15 })] })] }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({ children: [new Paragraph({ text: "" })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Account Number", size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.accountNumber}`, size: 15 })] })] }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({ children: [new Paragraph({ text: "" })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Bank Name", size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.intermediaryBank}`, size: 15 })] })] }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({ children: [new Paragraph({ text: "" })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Swift Code", size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.swiftCode}`, size: 15 })] })] }),
                                ],
                              }),
                              new TableRow({
                                children: [
                                  new TableCell({ children: [new Paragraph({ text: "" })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Bank Code", size: 15 })] })] }),
                                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `: ${staticData.bankCode}`, size: 15 })] })] }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),

              new Paragraph({ text: "", spacing: { after: 120 } }),

              // 7. Footer
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({ text: "Thank you for your business!\n", bold: true, size: 15 }),
                  new TextRun({ text: `LimoGenie Pte Ltd | ${staticData.footerInfo.address}\n`, size: 14 }),
                  new TextRun({ text: `Tel: ${staticData.footerInfo.tel} | Co. Reg. No. ${staticData.footerInfo.regNo}`, size: 14 }),
                ],
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const filename = `${(invoiceData.customerRef || invoiceData.invoiceNo || "Invoice").replace(/[/\\?%*:|"<>]/g, "-")}.docx`;
      saveAs(blob, filename);
    } catch (err) {
      console.error("Failed to generate DOCX:", err);
      setAlert({ show: true, message: "Error generating Word Document. Please try again.", type: "danger" });
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Access Control...</div>;

  if (!isSuperAdmin) {
    return (
      <Container className="mt-5">
        <UncontrolledAlert color="danger">
          <strong>Access Denied:</strong> Only Superadmin users can access the Manual Invoice Generator.
        </UncontrolledAlert>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-3 no-print">
            <Col className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Manual Invoice Generator</h4>
              <div className="d-flex gap-2">
                <Button color="primary" onClick={handleDownloadDocx} className="no-print">
                  <i className="ri-file-word-line me-1"></i> Download Word (.docx)
                </Button>
                <Button color="secondary" onClick={handlePrint} className="no-print">
                  <i className="ri-printer-line me-1"></i> Print / Save PDF
                </Button>
              </div>
            </Col>
          </Row>

          {alert.show && (
            <UncontrolledAlert color={alert.type} onClose={() => setAlert({ show: false })} className="no-print">
              {alert.message}
            </UncontrolledAlert>
          )}

          <Row>
            {/* INPUT FORM (HIDDEN DURING PRINT) */}
            <Col lg={5} className="no-print">
              <Card>
                <CardHeader className="bg-light">
                  <h5 className="card-title mb-0">Invoice Inputs</h5>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    <h6 className="text-primary mb-3">1. Requestor Details</h6>
                    <Row className="g-3 mb-3">
                      <Col md={12}>
                        <Label>Customer / Requestor Name (*)</Label>
                        <Input
                          type="text"
                          name="requestorName"
                          value={invoiceData.requestorName}
                          onChange={handleInputChange}
                          required
                        />
                      </Col>
                      <Col md={12}>
                        <Label>Address</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          name="requestorAddress"
                          value={invoiceData.requestorAddress}
                          onChange={handleInputChange}
                        />
                      </Col>
                      <Col md={12}>
                        <Label>Attention To</Label>
                        <Input
                          type="text"
                          name="attention"
                          value={invoiceData.attention}
                          onChange={handleInputChange}
                        />
                      </Col>
                    </Row>

                    <h6 className="text-primary mb-3">2. Invoice Reference</h6>
                    <Row className="g-3 mb-3">
                      <Col md={4}>
                        <Label>Date (*)</Label>
                        <Input
                          type="text"
                          name="invoiceDate"
                          value={invoiceData.invoiceDate}
                          onChange={handleInputChange}
                          required
                        />
                      </Col>
                      <Col md={4}>
                        <Label>Inv No. (*)</Label>
                        <Input
                          type="text"
                          name="invoiceNo"
                          value={invoiceData.invoiceNo}
                          onChange={handleInputChange}
                          required
                        />
                      </Col>
                      <Col md={4}>
                        <Label>Customer Ref (*)</Label>
                        <Input
                          type="text"
                          name="customerRef"
                          value={invoiceData.customerRef}
                          onChange={handleInputChange}
                          required
                        />
                      </Col>
                      <Col md={12}>
                        <Label>Payment Term</Label>
                        <Input
                          type="text"
                          name="paymentTerms"
                          value={invoiceData.paymentTerms}
                          onChange={handleInputChange}
                        />
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="text-primary mb-0">3. Line Items</h6>
                      <Button color="soft-success" size="sm" onClick={addItem}>
                        + Add Item
                      </Button>
                    </div>

                    {invoiceData.items.map((item, index) => (
                      <Card key={item.id} className="border p-2 mb-2 bg-light">
                        <Row className="g-2">
                          <Col md={12}>
                            <Label className="fs-12 mb-1">Description</Label>
                            <Input
                              type="textarea"
                              rows={2}
                              value={item.description}
                              onChange={(e) => handleItemChange(index, "description", e.target.value)}
                              required
                            />
                          </Col>
                          <Col md={3}>
                            <Label className="fs-12 mb-1">Quantity</Label>
                            <Input
                              type="number"
                              step="any"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            />
                          </Col>
                          <Col md={3}>
                            <Label className="fs-12 mb-1">Unit Label</Label>
                            <Input
                              type="text"
                              placeholder="e.g. rides"
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                            />
                          </Col>
                          <Col md={3}>
                            <Label className="fs-12 mb-1">Unit Price SGD</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                            />
                          </Col>
                          <Col md={3}>
                            <Label className="fs-12 mb-1">Total SGD (*)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.totalAmount}
                              onChange={(e) => handleItemChange(index, "totalAmount", e.target.value)}
                              required
                            />
                          </Col>
                          {invoiceData.items.length > 1 && (
                            <Col md={12} className="text-end">
                              <Button color="danger" size="sm" outline onClick={() => removeItem(index)}>
                                Remove Item
                              </Button>
                            </Col>
                          )}
                        </Row>
                      </Card>
                    ))}

                    <Button color="success" className="w-100 mt-4" type="submit">
                      Save Invoice
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* EXACT MATCH PDF PREVIEW LAYOUT */}
            <Col lg={7} id="printable-invoice">
              <Card className="p-4 border bg-white text-dark style-invoice-card">
                {/* 1. Header (Logo & Title) */}
                <Row className="align-items-start mb-3">
                  <Col xs={6}>
                    <img
                      src={logoImg}
                      alt="LimoGenie Logo"
                      style={{ height: "55px", objectFit: "contain" }}
                    />
                  </Col>
                  <Col xs={6} className="text-end">
                    <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "1px" }}>
                      INVOICE
                    </h3>
                  </Col>
                </Row>

                {/* 2. Customer Info (Left) & Invoice Meta (Right) */}
                <Row className="mb-3 fs-12">
                  <Col xs={7}>
                    <div className="fw-bold text-dark">To: {invoiceData.requestorName}</div>
                    <div style={{ whiteSpace: "pre-line" }} className="text-dark">
                      {invoiceData.requestorAddress}
                    </div>
                    {invoiceData.attention && (
                      <div className="mt-1 text-dark">
                        <strong>Attention:</strong> {invoiceData.attention}
                      </div>
                    )}
                  </Col>
                  <Col xs={5}>
                    <Table bordered responsive size="sm" className="mb-0 fs-11 border-dark text-dark">
                      <tbody>
                        <tr>
                          <td style={{ width: "45%" }} className="fw-bold bg-light py-1">Date:</td>
                          <td className="py-1">{invoiceData.invoiceDate}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold bg-light py-1">Inv No.:</td>
                          <td className="py-1">{invoiceData.invoiceNo}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold bg-light py-1">Customer ref:</td>
                          <td className="py-1">{invoiceData.customerRef}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                {/* 3. Services / Line Items Table */}
                <Table bordered responsive className="align-middle mb-2 fs-11 border-dark text-dark">
                  <thead className="table-light border-dark">
                    <tr>
                      <th style={{ width: "8%" }} className="text-center py-1">S/no</th>
                      <th className="py-1">Description</th>
                      <th style={{ width: "15%" }} className="text-center py-1">Quantity</th>
                      <th style={{ width: "17%" }} className="text-end py-1">Unit Price SGD</th>
                      <th style={{ width: "20%" }} className="text-end py-1">Total Amount SGD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, index) => (
                      <tr key={index}>
                        <td className="text-center py-1">{index + 1}</td>
                        <td style={{ whiteSpace: "pre-line" }} className="py-1">{item.description}</td>
                        <td className="text-center py-1">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="text-end py-1">
                          {item.unitPrice !== "" && !isNaN(Number(item.unitPrice))
                            ? Number(item.unitPrice).toFixed(2)
                            : ""}
                        </td>
                        <td className="text-end fw-semibold py-1">
                          {Number(item.totalAmount || 0).toLocaleString("en-SG", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="4" className="text-end fw-bold py-1">
                        Total Amount
                      </td>
                      <td className="text-end fw-bold py-1">
                        {calculateTotal().toLocaleString("en-SG", {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* 4. Amount In Words (AUTOMATED) */}
                <div className="fs-11 mb-2 text-dark">
                  <p className="mb-1">
                    <strong>(In words: {amountInWordsText})</strong>
                  </p>
                </div>

                {/* 5. Payment Terms, Stamp & Signature Block */}
                <Row className="align-items-end my-2 fs-11">
                  <Col xs={6}>
                    <p className="mb-1 text-dark">
                      <strong><u>Payment Term:</u></strong>
                    </p>
                    <p className="mb-0 text-dark"> 
                      {invoiceData.paymentTerms}
                    </p>
                  </Col>
                  <Col xs={6} className="text-center">
                    <div className="d-flex align-items-center justify-content-center gap-2" style={{ minHeight: "60px" }}>
                      <img
                        src={stampImg}
                        alt="Company Stamp"
                        style={{ maxHeight: "65px", objectFit: "contain" }}
                      />
                      <img
                        src={signatureImg}
                        alt="Authorised Signature"
                        style={{ maxHeight: "40px", objectFit: "contain" }}
                      />
                    </div>
                    <div className="border-top border-dark pt-1 mt-1 fs-11 fw-bold text-dark">
                      {staticData.signatureTitle}
                    </div>
                  </Col>
                </Row>

                {/* 6. Beneficiary & Bank Details Block */}
                <div className="border border-dark p-2 mb-2 fs-10 text-dark bg-light">
                  <div className="fw-bold mb-1 text-decoration-underline fs-11">
                    Please make payment to:
                  </div>
                  <Table size="sm" className="table-borderless mb-0 fs-10 text-dark">
                    <tbody>
                      <tr>
                        <td style={{ width: "25%" }} className="fw-bold align-top py-0">
                          Beneficiary Details
                        </td>
                        <td style={{ width: "25%" }} className="py-0">Company Name</td>
                        <td className="py-0">: {staticData.companyName}</td>
                      </tr>
                      <tr>
                        <td className="py-0"></td>
                        <td className="py-0">Company Address</td>
                        <td className="py-0">: {staticData.companyAddress}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold align-top pt-1 pb-0">Bank Details</td>
                        <td className="pt-1 pb-0">Account Name</td>
                        <td className="pt-1 pb-0">: {staticData.beneficiaryBank}</td>
                      </tr>
                      <tr>
                        <td className="py-0"></td>
                        <td className="py-0">Account Number</td>
                        <td className="py-0">: {staticData.accountNumber}</td>
                      </tr>
                      <tr>
                        <td className="py-0"></td>
                        <td className="py-0">Bank Name</td>
                        <td className="py-0">: {staticData.intermediaryBank}</td>
                      </tr>
                      <tr>
                        <td className="py-0"></td>
                        <td className="py-0">Swift Code</td>
                        <td className="py-0">: {staticData.swiftCode}</td>
                      </tr>
                      <tr>
                        <td className="py-0"></td>
                        <td className="py-0">Bank Code</td>
                        <td className="py-0">: {staticData.bankCode}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* 7. Footer */}
                <div className="text-center border-top border-dark pt-1 fs-10 text-dark">
                  <p className="fw-bold mb-0">Thank you for your business!</p>
                  <p className="mb-0">
                    LimoGenie Pte Ltd | {staticData.footerInfo.address}
                  </p>
                  <p className="mb-0">
                    Tel: {staticData.footerInfo.tel} | Co. Reg. No. {staticData.footerInfo.regNo}
                  </p>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        @media print {
          @page {
            margin: 0;
            size: A4 portrait;
          }

          html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            height: 100%;
            overflow: hidden;
          }

          .no-print, 
          .navbar-menu, 
          .topbar, 
          .footer,
          .page-content-wrapper,
          .page-title-box,
          header,
          nav {
            display: none !important;
          }

          .page-content {
            padding: 0 !important;
            margin: 0 !important;
          }

          #printable-invoice {
            width: 100% !important;
            max-width: 100% !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            margin: 0 !important;
            padding: 10mm 15mm !important;
            box-shadow: none !important;
            border: none !important;
          }

          #printable-invoice .card {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>
    </React.Fragment>
  );
};

export default ManualInvoice;